import log from 'salvo-lite/log'
import cssClasses from '../../helpers/cssClasses'

import { compatibilityService, UNKNOWN_COMPATIBILITY_RESULT } from '@scripts/services/compatibilityService'
import { garageService } from '../../services/garageService'

const TAG = 'CompatibilityCheck'

const AVAILABLE_RESULT_TYPES = [
    'definitelyCompatible',
    'possiblyCompatible',
    'compatible',
    'unknown',
    'novehicle',
    'unrecognized',
    'incompatible',
    'possiblyIncompatible',
    'definitelyIncompatible'
]
const ATTR_PRODUCT_ID = 'data-compatibility-check-product'
const ATTR_STATE_ELEMENT = 'data-compatibility-check-state'
const ATTR_SHOW_ELEMENT = 'data-compatibility-check-show'

/**
 * A component which contains elements which should be hidden or shown in response to the compatibility of a product with the customer's active vehicle.
 *
 * Usage:
 *     <div
 *         data-compatibility-check
 *         data-compatibility-check-product="gid://shopify/Product/{{ product.id }}">
 *         <div data-compatibility-check-show="definitelyCompatible">
 *             Visible when the product is definitely compatible
 *         </div>
 *         <div data-compatibility-check-show="possiblyCompatible">
 *             Visible when the product is possibly compatible
 *         </div>
 *         <div data-compatibility-check-show="compatible">
 *             Visible when the product is possibly or definitely compatible
 *         </div>
 *         <div data-compatibility-check-show="unknown">
 *             Visible when compatibility is unknown (i.e. unknown/no product/vehicle)
 *         </div>
 *         <div data-compatibility-check-show="novehicle">
 *             Visible when compatibility is unknown and no active vehicle
 *         </div>
 *         <div data-compatibility-check-show="unrecognized">
 *             Visible when compatibility is unknown and active vehicle
 *         </div>
 *         <div data-compatibility-check-show="incompatible">
 *             Visible when the product is possibly or definitely incompatible
 *         </div>
 *         <div data-compatibility-check-show="possiblyIncompatible">
 *             Visible when the product is possibly incompatible
 *         </div>
 *         <div data-compatibility-check-show="definitelyIncompatible">
 *             Visible when the product is definitely incompatible
 *         </div>
 *         <div data-compatibility-check-state>
 *             This element will have [data-compatibility-check-state-*] values applied (useful for styling)
 *         </div>
 *     </div>
 * Notes:
 * - Result elements are optional and may appear anywhere in the subtree any number of times
 * - Do not nest this component
 */
export default class CompatibilityCheck {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem

        this._productId = elem.getAttribute(ATTR_PRODUCT_ID)
        this._result = UNKNOWN_COMPATIBILITY_RESULT
        this._showElements = {} // {[key: ResultType]: Element[]}
        this._stateElements = [] // Element[]
        this._compatSubscriptionUnsub = null // CompatibilitySubscription|null

        this.reloadElements()

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Get 'state element' children. This will have an attribute will be updated in sync with compat status.
     */
    _loadStateElements () {
        const els = this._elem.querySelectorAll(`[${ATTR_STATE_ELEMENT}]`)
        this._stateElements = Array.from(els)
    }

    _updateStateElements () {
        const stateAttributes = this._determineStateAttributes()
        for (let el of this._stateElements) {
            for (let key of Object.keys(stateAttributes)) {
                if (stateAttributes[key]) {
                    el.setAttribute(key, '')
                } else {
                    el.removeAttribute(key)
                }
            }
        }
    }
    _determineStateAttributes () {
        const activeResultTypes = this._determineVisibleResultTypes()
        const r = {}
        for (let resultType of AVAILABLE_RESULT_TYPES) {
            r[`${ATTR_STATE_ELEMENT}-${resultType}`] = activeResultTypes.indexOf(resultType) > -1
            console.log(r)
        }
        return r
    }

    /**
     * Get 'show element' children. These will be shown/hidden in sync with compat status.
     */
    _loadShowElements () {
        this._showElements = {}

        const els = this._elem.querySelectorAll(`[${ATTR_SHOW_ELEMENT}]`)
        for (let el of els) {
            const resultType = el.getAttribute(ATTR_SHOW_ELEMENT)
            if (!this._validateResultType(resultType)) {
                log.error(TAG, `Unknown result type ${resultType}`, {
                    resultType,
                    el
                })
                continue
            }
            if (!this._showElements[resultType]) {
                this._showElements[resultType] = []
            }
            this._showElements[resultType].push(el)
        }
    }
    _validateResultType (resultType) {
        return AVAILABLE_RESULT_TYPES.indexOf(resultType) > -1
    }

    /**
     * Compat state has changed, show/hide children
     */
    _updateShowElements () {
        const visibleResultTypes = this._determineVisibleResultTypes()
        for (let resultType in this._showElements) {
            const els = this._showElements[resultType]
            const visible = visibleResultTypes.indexOf(resultType) > -1

            for (let el of els) {
                el.style.display = visible ? '' : 'none'
            }
        }
    }
    _determineVisibleResultTypes () {
        const r = []
        if (this._result.definitelyCompatible) {
            if (this._result.possiblyCompatible) {
                r.push('compatible')
            }
            r.push('definitelyCompatible')
        } else if (this._result.possiblyCompatible) {
            r.push('possiblyCompatible')
        }

        if (this._result.definitelyIncompatible) {
            if (this._result.possiblyIncompatible) {
                r.push('incompatible')
            }
            r.push('definitelyIncompatible')
        } else if (this._result.possiblyIncompatible) {
            r.push('possiblyIncompatible')
        }

        if (!this._result.definitelyCompatible &&
            !this._result.possiblyCompatible &&
            !this._result.possiblyIncompatible &&
            !this._result.definitelyIncompatible) {
            r.push('unknown')

            if (!garageService.activeVehicle) {
                r.push('novehicle')
            } else {
                r.push('unrecognized')
            }
        }

        log.debug(TAG, 'Determined visibility', {
            r,
            result: this._result
        })
        return r
    }

    onInit () {
        this._compatSubscriptionUnsub = compatibilityService.subscribeCompatibility(this._productId, (result) => {
            this._result = result
            this._updateShowElements()
            this._updateStateElements()
            this._elem.classList.remove(cssClasses.hidden)
        })
    }

    destroy () {
        if (this._compatSubscriptionUnsub) {
            this._compatSubscriptionUnsub()
        }
    }

    reloadElements () {
        this._loadShowElements()
        this._loadStateElements()
        this._updateShowElements()
        this._updateStateElements()
    }
}



// WEBPACK FOOTER //
// ./src/resources/scripts/components/garage/CompatibilityCheck.js